// Everything this console does that touches the protocol.
//
// One file, because the rules are easier to keep straight in one place
// than scattered across screens: which profile's field rides in which
// wire slot (§20.8), what a plan's identity is, that a ticket is sealed
// before it exists as bytes anyone can hold (§20.9), and that a route's
// root never goes anywhere near a driver (§21.2).
//
// Nothing here renders. Screens ask for artifacts and get artifacts.

import { profileFor, THREAD_MODE, type ProfileId, type StopFieldId } from "@wayfind/core";
import type { WayfindSession } from "@wayfind/core";
import { config, nowSeconds } from "../config";
import {
  dispatchBundle,
  dispatchEngine,
  fromHex,
  toHex,
  type DecodedOffer,
  type DecodedTicket,
  type DispatchBundle,
  type DispatchRouteEngine,
  type PlanInput,
  type PlanStopInput
} from "./engine";
import type { StoredStop } from "./state";

const encoder = new TextEncoder();

/** UTF-8 bytes, truncated on a character boundary. The codec caps are hard. */
function clampBytes(text: string, maxBytes: number): string {
  let out = text.trim();
  while (encoder.encode(out).length > maxBytes) out = out.slice(0, -1).trimEnd();
  return out;
}

function fieldText(stop: StoredStop, id: StopFieldId): string {
  const value = stop.fields[id];
  return typeof value === "string" ? value.trim() : value == null ? "" : String(value);
}

function fieldNumber(stop: StoredStop, id: StopFieldId): number | null {
  const value = stop.fields[id];
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function fieldFlag(stop: StoredStop, id: StopFieldId): boolean {
  return stop.fields[id] === true;
}

/**
 * A stored stop as plan bytes want it.
 *
 * The profile decides which fields exist and `StopFieldDefinition.wire`
 * decides where each one rides. Two profiles need a sentence:
 *
 * - **Pharmacy.** *Check ID* and *cold chain* are flags with no wire
 *   slot of their own — §20.8 has four fields and neither is a boolean.
 *   They go in front of the instruction line, in words, because that is
 *   the line a driver reads at the door. The interface says so.
 * - **School.** No parcels field and no per-stop contact, so neither wire
 *   slot is filled at all. The stop's *name* is the label, and the number
 *   of children expected rides in the instruction line — a stop is a
 *   place with a number of children at it, and never a name.
 */
export function planStopFor(profile: ProfileId, stop: StoredStop): PlanStopInput {
  const definition = profileFor(profile);
  const has = (id: StopFieldId) => definition.stopFields.some(field => field.id === id);

  const label = clampBytes(
    (has("stopName") ? fieldText(stop, "stopName") : "") || stop.label,
    48
  );

  let orderRef: string | null = null;
  for (const id of ["orderRef", "waybill", "rxReference"] as StopFieldId[]) {
    if (has(id) && fieldText(stop, id)) orderRef = clampBytes(fieldText(stop, id), 24);
  }

  let parcels: number | null = null;
  for (const id of ["bags", "parcels"] as StopFieldId[]) {
    if (has(id)) parcels = fieldNumber(stop, id) ?? parcels;
  }

  const notes: string[] = [];
  if (has("identityCheck") && fieldFlag(stop, "identityCheck")) notes.push("ID check");
  if (has("coldChain") && fieldFlag(stop, "coldChain")) notes.push("Cold chain");
  if (has("studentCount")) {
    const count = fieldNumber(stop, "studentCount");
    if (count != null) notes.push(`${count} expected`);
  }
  for (const id of ["note", "instructions"] as StopFieldId[]) {
    if (has(id) && fieldText(stop, id)) notes.push(fieldText(stop, id));
  }
  const instructions = notes.length ? clampBytes(notes.join(" · "), 64) : null;

  const contact = has("contact") && fieldText(stop, "contact")
    ? clampBytes(fieldText(stop, "contact"), 24)
    : null;

  return {
    lat: stop.lat,
    lon: stop.lon,
    label,
    ...(orderRef ? { orderRef } : {}),
    ...(parcels != null ? { parcels } : {}),
    ...(instructions ? { instructions } : {}),
    ...(contact ? { contact } : {})
  };
}

export function planInputFor(
  profile: ProfileId,
  stops: StoredStop[],
  options: { travelMode?: number; dwellSeconds?: number; readyInMinutes?: number | null } = {}
): PlanInput {
  const planStops = stops.map(stop => planStopFor(profile, stop));
  // A restaurant's whole rhythm: the rider is moving before the food is
  // bagged, so the first stop carries the moment it will actually be
  // ready. It is a plan field (§20.2 field 8), which is what makes it a
  // *scheduled* arrival a customer's own device can read rather than a
  // promise this console made in prose.
  if (options.readyInMinutes != null && planStops.length) {
    const first = planStops[0]!;
    first.plannedUnixSeconds = nowSeconds() + options.readyInMinutes * 60;
  }
  return {
    stops: planStops,
    dwellSeconds: options.dwellSeconds ?? 120,
    travelMode: options.travelMode ?? 1
  };
}

/**
 * The identity of a **stop set**, for "the same round three days
 * running".
 *
 * Deliberately not `planRef`. A restaurant sending the same five
 * addresses with fresh order numbers every evening has run the same
 * round three times and produced three different plan hashes; a repeat
 * detector built on plan bytes would never fire for the operation that
 * most wants it to. So this hashes rounded coordinates, order-insensitive
 * — a round driven backwards is the same round.
 */
export function planKeyFor(stops: StoredStop[]): string {
  const points = stops
    .map(stop => `${stop.lat.toFixed(4)},${stop.lon.toFixed(4)}`)
    .sort();
  let hash = 0x811c9dc5;
  for (const character of points.join("|")) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${points.length}:${hash.toString(16).padStart(8, "0")}`;
}

export interface Ordering {
  /** Indices into the stops handed in, in the order they should be driven. */
  order: number[];
  totalSeconds: number;
  totalMeters: number;
}

export interface Dispatcher {
  threads: DispatchBundle;
  engine: DispatchRouteEngine;
  epoch32: Uint8Array;
  epochPrefix8: Uint8Array;

  order(stops: StoredStop[]): Promise<Ordering | null>;
  planBytes(plan: PlanInput): Uint8Array;
  planRefHex(planBytes: Uint8Array): string;

  issueJob(params: IssueJobParams): Promise<IssuedJob>;
  /** §20.5 — the same ticket, handed to a different phone. */
  reseal(params: ResealParams): Promise<{ sealedB64: string; driverUrl: string; ticket: DecodedTicket }>;
  openTicket(sealedB64: string, devicePrivateKeyHex: string): Promise<DecodedTicket>;
  /**
   * §20.7 — the doorstep photo behind a commitment, as an image this
   * console can draw.
   *
   * Every job is sealed to this console as well as to the driver, which
   * is the whole reason this is possible: opening the ticket yields the
   * run seed, and the run seed is what the photo key derives from. A
   * customer holding the tracking link has the commitment and cannot do
   * this, which is the §20.7 trade stated as an implementation.
   */
  openProofPhoto(params: {
    sealedB64: string;
    consolePrivateKeyHex: string;
    /** The sealed blob, as `follow.proof.fetch` returned it. */
    sealed: Uint8Array;
    /** Which doorstep, 1-based in the plan's own order. */
    stopIndex: number;
    hash: string;
  }): Promise<{ bytes: Uint8Array; type: string }>;
  issueOffer(params: IssueOfferParams): Promise<IssuedOfferResult>;
  openOffer(base64OrUrl: string): DecodedOffer;
  checkAward(offer: DecodedOffer, ticket: DecodedTicket): { ok: boolean; reason: string | null };

  newRouteRoot(): Promise<{ seedHex: string; publicKeyHex: string }>;
  /**
   * Takes the root **seed**, not the public key: the link carries a
   * capability secret derived from the seed beside the verification key,
   * so only the holder of the root can mint it — which is the depot, and
   * exactly who this method is for.
   */
  termLink(rootSeedHex: string, notAfter: number): Promise<string>;
  /** §21.11 — one service day of a route, as one sealed artifact. */
  issueRouteDay(params: IssueRouteDayParams): Promise<IssuedRouteDay>;
  /** What a sealed artifact says it is, read back out of the bytes. */
  readTicket(sealedB64: string, devicePrivateKeyHex: string): Promise<ReadTicket>;
}

export interface IssueJobParams {
  issuerSeedHex: string;
  plan: PlanInput;
  /** X25519 public keys, hex. The awarded device, and usually this console. */
  recipients: string[];
  notAfter: number;
  mode: number;
  /** §20.10 — travels inside the sealed ticket and nowhere else. */
  bootstrap?: string[] | null;
  /** Whether the *customer's* link carries the public `?b=` hint. */
  hintCustomerLink?: boolean;
  trackBaseUrl: string;
}

export interface IssuedJob {
  sealedB64: string;
  driverUrl: string;
  trackUrl: string;
  jobIdHex: string;
  planRefHex: string;
  ticket: DecodedTicket;
  recipientCount: number;
}

export interface ResealParams {
  sealedB64: string;
  /** This console's own device key — the reason it sealed to itself. */
  consolePrivateKeyHex: string;
  recipients: string[];
}

export interface IssueOfferParams {
  issuerSeedHex: string;
  /**
   * The job being advertised. Passing the ticket rather than a fresh
   * plan is what makes `jobId` join the two artifacts in both
   * directions, so the winner can prove the award is the job that was
   * advertised.
   */
  ticket: DecodedTicket;
  totalMeters: number;
  payMinor?: number | null;
  currency?: string | null;
  label?: string | null;
}

export interface IssuedOfferResult {
  url: string;
  base64url: string;
  offer: DecodedOffer;
  jobIdHex: string;
}

export interface IssueRouteDayParams {
  /**
   * §21.11 — the issuer of a route-day ticket **is** the route root, and
   * that is not a tightening: minting the certificate and deriving the
   * day seed both need this seed, so nobody who could issue one was
   * signing as anything else anyway.
   */
  rootSeedHex: string;
  /**
   * The term's plan, **byte for byte**. The day seed is derived from
   * `(rootSeed, planRef, serviceDay)` and minting re-derives it, so a
   * plan rebuilt from the stops — a changed profile, a different travel
   * mode, a stop edited since — is a different `planRef` and a refusal.
   * Handing over the stored bytes is what makes that unreachable.
   */
  planBytes: Uint8Array;
  serviceDay: number;
  /** The day's authority window. ≤48 h, and it bounds the run. */
  notBefore: number;
  notAfter: number;
  /**
   * The **term's** end, which is the ticket's own `notAfter` and what the
   * 78-byte link is cut to. Not the day's: the link must be the same
   * bytes tomorrow, and it is derived from this.
   */
  termNotAfter: number;
  /** X25519 device keys, hex. Whoever is driving, and this console. */
  recipients: string[];
  mode: number;
  bootstrap?: string[] | null;
}

export interface IssuedRouteDay {
  sealedB64: string;
  driverUrl: string;
  /**
   * The route's own term link, read back **off the ticket** rather than
   * remembered. On a route-day ticket `ticketFollowLink` returns the
   * root's v2 link, so this is the same 78 bytes every day of the term.
   */
  trackUrl: string;
  jobIdHex: string;
  serviceDay: number;
  notBefore: number;
  notAfter: number;
  recipientCount: number;
}

/** What a sealed artifact turns out to be once it is opened. */
export interface ReadTicket {
  kind: string | null;
  routeDay: boolean;
  serviceDay: number | null;
  stopCount: number;
  /** The link these bytes derive — the term's, on a route day. */
  followUrl: string;
}

/**
 * Opens the dispatcher's half of the engine.
 *
 * Returns null without a route graph, and that is not a failure to
 * apologise for: a ticket is bound to the graph's epoch (§20.6) and a
 * round is ordered by the graph, so a console with no graph genuinely
 * cannot mint a job. The screen says which of the two states it is in.
 */
export function createDispatcher(session: WayfindSession): Dispatcher | null {
  const engine = dispatchEngine(session.engine);
  if (!engine?.root?.sourceHash) return null;
  const threads = dispatchBundle(session.bundles);
  // The session's epoch, not the graph's content hash. They are the same
  // string until a dataset is configured; after that the content hash is
  // the *build* and this is the *map*, and a ticket has to name the map or
  // it is refused by the driver standing at the door the morning after a
  // rebuild. The engine is still required above — a console with no graph
  // cannot order a round — but it is no longer what identifies the map.
  const epoch32 = fromHex(session.epochHex ?? engine.root.sourceHash);
  const epochPrefix8 = epoch32.subarray(0, 8);

  return {
    threads,
    engine,
    epoch32,
    epochPrefix8,

    /**
     * The order the stops should be driven in.
     *
     * `itinerary({ openEnd: true })` is §20.2's intended producer, and its
     * stop 0 is an anchor rather than a delivery. This console has no
     * depot concept at rung 1 and will not invent one, so the anchor is
     * the **first address the operator typed** — which is also the one
     * they can move, since a hand-sorted round always wins over this.
     */
    async order(stops) {
      if (stops.length < 3) {
        if (stops.length < 2) return null;
        const leg = await engine
          .route({ from: stops[0]!, to: stops[1]!, geometry: true })
          .catch(() => null);
        return leg
          ? { order: [0, 1], totalSeconds: leg.seconds, totalMeters: leg.distanceMeters ?? 0 }
          : null;
      }
      const points = stops.map(stop => ({ lat: stop.lat, lon: stop.lon }));
      const result = await engine
        .itinerary({ stops: points, openEnd: true })
        .catch(() => null);
      if (!result?.order?.length) return null;
      return {
        order: result.order,
        totalSeconds: result.totalSeconds,
        totalMeters: result.totalMeters
      };
    },

    planBytes(plan) {
      return threads.encodeThreadPlan(plan);
    },

    planRefHex(planBytes) {
      return toHex(threads.planRefOf(planBytes));
    },

    async issueJob(params) {
      const recipients = params.recipients.map(hex => fromHex(hex));
      const issued = await threads.issueSealedTicket({
        issuerSeed: fromHex(params.issuerSeedHex),
        epochPrefix8,
        plan: params.plan,
        notAfter: params.notAfter,
        mode: params.mode,
        ...(params.bootstrap?.length ? { bootstrap: params.bootstrap } : {}),
        recipients
      });
      // §5.4 and §20.10: the capability goes in the fragment, and the
      // bootstrap hint — a *location*, not a key — goes in the query, and
      // only when the operator asked for it on this link.
      const trackUrl = threads.threadLinkUrl(params.trackBaseUrl, issued.link, {
        bootstrap: params.hintCustomerLink ? params.bootstrap ?? null : null
      });
      return {
        sealedB64: issued.base64url,
        driverUrl: issued.driverUrl,
        trackUrl,
        jobIdHex: issued.jobIdHex,
        planRefHex: toHex(issued.ticket.plan.planRef),
        ticket: issued.ticket,
        recipientCount: issued.recipientCount
      };
    },

    /**
     * A handover is the *same ticket*, sealed to a different phone —
     * never a new job. The customer's link came out of the run seed
     * inside these bytes, so reissuing would break every link already
     * sent, which is exactly what §20.1 exists to avoid.
     *
     * This is only possible because the console sealed the job to itself
     * when it issued it. A dispatcher that did not cannot hand the work
     * on, and that is a property of the bytes rather than of this app.
     */
    async reseal(params) {
      const sealed = threads.base64UrlToBytes(params.sealedB64);
      const inner = await threads.openSealedTicket(sealed, fromHex(params.consolePrivateKeyHex));
      const resealed = await threads.sealTicket(inner, params.recipients.map(hex => fromHex(hex)));
      return {
        sealedB64: threads.bytesToBase64Url(resealed),
        driverUrl: threads.sealedTicketUrl(resealed),
        ticket: threads.decodeThreadTicket(inner)
      };
    },

    async openTicket(sealedB64, devicePrivateKeyHex) {
      const inner = await threads.openSealedTicket(
        threads.base64UrlToBytes(sealedB64),
        fromHex(devicePrivateKeyHex)
      );
      return threads.decodeThreadTicket(inner);
    },

    async openProofPhoto({ sealedB64, consolePrivateKeyHex, sealed, stopIndex, hash }) {
      const inner = await threads.openSealedTicket(
        threads.base64UrlToBytes(sealedB64),
        fromHex(consolePrivateKeyHex)
      );
      const ticket = threads.decodeThreadTicket(inner);
      if (!ticket.privateSeed) {
        // A seedless ticket is an offer's shape, and an offer publishes
        // nothing — so there is no run behind it and no photo to open.
        throw new Error("This ticket carries no run seed, so it has no photo to open.");
      }
      // The planRef the photo key is scoped to is the plan **in the
      // ticket**, byte for byte. Rebuilding it from the stored stops
      // would be a different planRef through any change of profile or
      // travel mode, and the decrypt would fail with nothing to say.
      const bytes = await threads.openPhoto(sealed, {
        privateSeed: ticket.privateSeed,
        planRef: threads.planRefOf(ticket.planBytes),
        stopIndex,
        hash
      });
      // The library hands back exactly the bytes the phone sealed, and
      // the app that sealed them re-encoded the camera frame to JPEG
      // (§20.7 is explicit that stripping EXIF is the host's job). The
      // magic is checked rather than assumed, because drawing arbitrary
      // bytes as an image is how a viewer renders something nobody sent.
      const jpeg = bytes[0] === 0xff && bytes[1] === 0xd8;
      const png = bytes[0] === 0x89 && bytes[1] === 0x50;
      if (!jpeg && !png) throw new Error("That proof is not an image this console can draw.");
      return { bytes, type: jpeg ? "image/jpeg" : "image/png" };
    },

    async issueOffer(params) {
      const issued = await threads.issueJobOffer({
        issuerSeed: fromHex(params.issuerSeedHex),
        ticket: params.ticket,
        totalMeters: Math.round(params.totalMeters),
        payMinor: params.payMinor ?? null,
        currency: params.currency ?? null,
        label: params.label ?? null
      });
      return {
        url: issued.url,
        base64url: issued.base64url,
        offer: issued.offer,
        jobIdHex: issued.jobIdHex
      };
    },

    openOffer(value) {
      return threads.decodeJobOffer(value.includes("#") ? value.slice(value.lastIndexOf("#") + 1) : value);
    },

    checkAward(offer, ticket) {
      return threads.awardMatchesOffer(offer, ticket);
    },

    async newRouteRoot() {
      const keypair = await threads.generateThreadKeypair();
      return { seedHex: toHex(keypair.privateSeed), publicKeyHex: toHex(keypair.publicKey) };
    },

    /**
     * The one link a parent keeps. A v2 link over the route **root**,
     * minted once, expiring at the end of the term — never at the end of
     * a run. It is safe precisely because the root cannot publish
     * anything without a day certificate whose own window is at most 48 h.
     */
    async termLink(rootSeedHex, notAfter) {
      // Identity split from capability (thread authority): the public key
      // is what a subscriber verifies against, and the thread secret —
      // derived from the seed, never the key reused as one — is what
      // lets the link's holder read the run at all.
      const rootSeed = fromHex(rootSeedHex);
      const link = threads.routeFollowLink({
        threadSecret: await threads.deriveThreadSecret(rootSeed),
        rootPublicKey: await threads.publicKeyFromSeed(rootSeed),
        epochPrefix8,
        notAfter
      });
      return threads.threadLinkUrl(config.trackBaseUrl, link);
    },

    /**
     * One service day of a route, as **one sealed ticket** (§21.11).
     *
     * The certificate travels inside the signed preimage, so identity
     * comes from the route root it names, authority from the day seed
     * beside it, and the proof to publish from the same 157 bytes — with
     * nothing to lose separately and no ordering problem between them.
     *
     * The two things this console must get right are both here. The
     * `planRef` in the certificate has to be the `planRef` of the plan in
     * the ticket, which is why `planBytes` is handed over rather than
     * rebuilt — minting refuses the mismatch either way, and refusing at
     * the console beats refusing on a phone at 06:40. And the ticket's
     * `notAfter` is the **term**, not the day: `ticketFollowLink` cuts
     * the 78 bytes to it, and a link cut to today expires tonight.
     */
    async issueRouteDay(params) {
      const rootSeed = fromHex(params.rootSeedHex);
      const minted = await threads.mintDayCertificate({
        rootSeed,
        planRef: threads.planRefOf(params.planBytes),
        serviceDay: params.serviceDay,
        notBefore: params.notBefore,
        notAfter: params.notAfter
      });
      const issued = await threads.issueSealedTicket({
        issuerSeed: rootSeed,
        epochPrefix8,
        planBytes: params.planBytes,
        notAfter: params.termNotAfter,
        mode: params.mode,
        dayCertificate: minted.certificate,
        // The day's own authority: a CSPRNG seed minted with the
        // certificate, never derived from the root — compromise of one
        // generation must not expose another. The ticket carries it so
        // the driver can sign the day; the certificate is what makes a
        // subscriber accept those signatures.
        privateSeed: minted.daySeed,
        ...(params.bootstrap?.length ? { bootstrap: params.bootstrap } : {}),
        recipients: params.recipients.map(hex => fromHex(hex)),
        // The same base `termLink` uses, and deliberately not an
        // argument: the console compares what comes back against the
        // link it already sent, and a base that could differ between the
        // two would make that comparison about the wrong thing.
        baseUrl: config.trackBaseUrl
      });
      return {
        sealedB64: issued.base64url,
        driverUrl: issued.driverUrl,
        // `issued.url` is `ticketFollowLink`'s answer, and on a day
        // ticket that is the route's own v2 link. The console never
        // constructs this from the day key because it cannot.
        trackUrl: issued.url ?? "",
        jobIdHex: issued.jobIdHex,
        serviceDay: minted.certificate.serviceDay,
        notBefore: minted.certificate.notBefore,
        notAfter: minted.certificate.notAfter,
        recipientCount: issued.recipientCount
      };
    },

    /**
     * What a sealed artifact turns out to be, once opened.
     *
     * `classifyThreadArtifact` on the *sealed* bytes answers `routeDay:
     * null` — which sort of job is inside a PME1 is ciphertext, and that
     * is the honest answer rather than "an ordinary job". So this opens
     * it with a key that is entitled to first, and classifies the
     * plaintext, which can say.
     */
    async readTicket(sealedB64, devicePrivateKeyHex) {
      const inner = await threads.openSealedTicket(
        threads.base64UrlToBytes(sealedB64),
        fromHex(devicePrivateKeyHex)
      );
      const classified = threads.classifyThreadArtifact(inner);
      const ticket = threads.decodeThreadTicket(inner);
      const link = await threads.ticketFollowLink(ticket);
      return {
        kind: classified.kind,
        routeDay: classified.routeDay === true,
        serviceDay: classified.serviceDay,
        stopCount: ticket.plan.stops.length,
        followUrl: threads.threadLinkUrl(config.trackBaseUrl, link)
      };
    }
  };
}

export { THREAD_MODE };
